import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import useLocalStorage from './useLocalStorage'
import useSessionStorage from './sessionStorage'

export default function useMiddleware(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const { jwt, user, stores, moduleAccessesByStores } = useLocalStorage()
  const { originalRoute } = useSessionStorage()
  const hasToken = !!jwt.value
  const userSignUpConfig = Number(user.value?.configs?.['signupStep'])

  const requiresSignUp = () => {
    const isSignUpNotCompleted = userSignUpConfig < 2
    const pagesToExclude = ['SignUp', 'Autologin']

    const isSignUpRedirectionAllowed = to?.name && !pagesToExclude.includes(to.name.toString())

    const canRedirect = isSignUpNotCompleted && isSignUpRedirectionAllowed
    if (canRedirect) {
      next({ name: 'SignUp', query: from.query })
      return false
    }

    return true
  }

  const requiresSelfServe = () => {
    const selfServePages = to.matched.some((record) => record.meta.isSelfServe)
    if (!selfServePages) {
      return true
    }

    if (!!user.value.merchant.isSelfServe || to.name === 'Autologin') {
      return true
    } else {
      next({ name: 'Dashboard', query: to.query })
      return false
    }
  }

  const requiresAuth = () => {
    const requiredAuthPages = to.matched.some((record) => record.meta.requiresAuth)
    if (!requiredAuthPages) {
      return true
    }

    if (!hasToken) {
      originalRoute.value = to
      next({
        name: 'Login',
        params: { nextUrl: to.fullPath },
        query: to.query
      })
      return false
    }
    return true
  }

  const requiresGuest = () => {
    const requiredGuestPages = to.matched.some((record) => record.meta.guest)
    if (!requiredGuestPages) {
      return true
    }

    if (!hasToken || to.name === 'Autologin' || to.name === 'AccountBlocked') {
      return true
    }

    next({ name: 'Home', query: to.query })
    return false
  }

  const redirectsAfterLogin = () => {
    const hasOriginalRoute = originalRoute.value && Object.keys(originalRoute.value).length > 0
    if (hasToken && hasOriginalRoute) {
      next(originalRoute.value)
      originalRoute.value = null
      return false
    }
    return true
  }

  const redirectsLegacyReviews = () => {
    const legacyReviewPages: Record<string, string> = {
      ReviewList: 'reviews',
      ReviewAnalytics: 'analytics',
      ReviewSettings: 'settings'
    }
    const targetPage = to.name ? legacyReviewPages[to.name.toString()] : undefined
    if (!targetPage) {
      return true
    }

    const hasEReputationStore = stores.value.some((store) =>
      moduleAccessesByStores.value[store.id]?.includes('e-reputation')
    )
    const hasReviewsOnlyStore = stores.value.some(
      (store) =>
        moduleAccessesByStores.value[store.id]?.includes('review') &&
        !moduleAccessesByStores.value[store.id]?.includes('e-reputation')
    )

    if (hasEReputationStore && !hasReviewsOnlyStore) {
      next({ name: 'EReputation', params: { page: targetPage }, query: to.query })
      return false
    }

    return true
  }

  const activatedMiddlewares = [
    requiresSignUp,
    requiresSelfServe,
    requiresAuth,
    requiresGuest,
    redirectsAfterLogin,
    redirectsLegacyReviews
  ]

  return {
    activatedMiddlewares
  }
}
